import { useEffect, useRef, useState } from "react";
import { postJson } from "../api";
import { openExternal } from "../bootstrap";
import { describeError, t } from "../i18n";

/**
 * Hands the current room to a real browser window.
 *
 * Discord gives the Activity one slot on the call's stage, and a screen share
 * wants the same slot. Rather than fight that, this puts the map somewhere
 * Discord does not control -- another window, another monitor, a tablet -- while
 * the call keeps showing the stream.
 */

interface Props {
  session: string;
  instanceId: string;
  onClose: () => void;
}

interface Handoff {
  code: string;
  url: string;
  expiresInMs: number;
}

export function HandoffDialog({ session, instanceId, onClose }: Props) {
  const [handoff, setHandoff] = useState<Handoff | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<"idle" | "done" | "failed">("idle");
  const [remaining, setRemaining] = useState(0);
  const urlField = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    postJson<Handoff>("/api/handoff", { session, instanceId })
      .then((result) => {
        if (cancelled) return;
        setHandoff(result);
        setRemaining(Math.round(result.expiresInMs / 1000));
      })
      .catch((failure: unknown) => {
        if (!cancelled) setError(describeError(failure, "handoff.failed"));
      });
    return () => {
      cancelled = true;
    };
  }, [session, instanceId]);

  // Showing the clock avoids confusion when someone leaves the dialog open and
  // comes back to it much later.
  useEffect(() => {
    if (remaining <= 0) return;
    const timer = window.setInterval(() => setRemaining((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [remaining === 0]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  /**
   * The async Clipboard API needs a permission the Discord iframe does not
   * always grant, so fall back to the old selection trick, and if even that is
   * refused say so instead of looking like the button did nothing.
   */
  const copy = async () => {
    if (!handoff) return;

    const viaClipboardApi = async () => {
      if (!navigator.clipboard?.writeText) return false;
      try {
        await navigator.clipboard.writeText(handoff.url);
        return true;
      } catch {
        return false;
      }
    };

    /**
     * Selects the link either way. Even where the copy itself is refused the
     * text is left highlighted, so Ctrl+C finishes the job by hand.
     */
    const viaSelection = () => {
      const field = urlField.current;
      if (!field) return false;
      try {
        field.focus();
        field.select();
        field.setSelectionRange(0, handoff.url.length);
        return document.execCommand("copy");
      } catch {
        return false;
      }
    };

    const ok = (await viaClipboardApi()) || viaSelection();
    setCopyState(ok ? "done" : "failed");
    if (ok) window.setTimeout(() => setCopyState("idle"), 1800);
  };

  const minutes = Math.floor(remaining / 60);
  const seconds = String(remaining % 60).padStart(2, "0");
  const host = handoff ? handoff.url.replace(/^https?:\/\//, "").replace(/\/j\/.*$/, "") : "";

  return (
    <div className="modal" onClick={(event) => event.target === event.currentTarget && onClose()}>
      <div className="modal__panel" role="dialog" aria-modal="true" aria-label={t("handoff.title")}>
        <header className="modal__head">
          <strong>{t("handoff.title")}</strong>
          <button type="button" className="modal__close" onClick={onClose} aria-label={t("pin.close")}>
            ✕
          </button>
        </header>

        <p className="handoff__lede">
          {t("handoff.lede")}
        </p>

        {error && <p className="feedback__error">{error}</p>}

        {handoff && (
          <>
            <button type="button" className="handoff__open" onClick={() => void openExternal(handoff.url)}>
              {t("handoff.open")}
            </button>

            <div className="handoff__or">{t("handoff.or")}</div>

            <div className="handoff__code" aria-label={t("handoff.title")}>
              {handoff.code}
            </div>
            <p className="handoff__hint">{t("handoff.codeHint", { host })}</p>

            <input
              ref={urlField}
              className="handoff__url"
              value={handoff.url}
              readOnly
              spellCheck={false}
              aria-label={t("handoff.title")}
              onFocus={(event) => event.currentTarget.select()}
            />

            <button type="button" className="handoff__copy" onClick={() => void copy()}>
              {copyState === "done"
                ? t("handoff.copied")
                : copyState === "failed"
                  ? t("handoff.copyFailed")
                  : t("handoff.copy")}
            </button>

            <p className="handoff__note">
              {t("handoff.expiry", { time: `${minutes}:${seconds}` })}
            </p>
            <p className="invite__warn">{t("handoff.warn")}</p>
          </>
        )}

        {!handoff && !error && <p className="handoff__note">{t("handoff.creating")}</p>}
      </div>
    </div>
  );
}
